export const earthRadius = 6378137;
export const nauticalMile = 1852;

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

export function round(val: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(val * factor) / factor;
}

function toRad(deg: number): number {
  return deg * DEG_TO_RAD;
}

function normalizeAngle(angle: number): number {
  let a = angle % 360;
  if (a < 0) a += 360;
  return a;
}

export function getDistance(startPoint: number[], endPoint: number[]): number {
  const [lon1, lat1] = startPoint;
  const [lon2, lat2] = endPoint;

  if (round(lat1, 6) === round(lat2, 6) && round(lon1, 6) === round(lon2, 6)) {
    return 0;
  }

  const cosLat1 = Math.cos(toRad(lat1));
  const cosLat2 = Math.cos(toRad(lat2));
  const sinLat1 = Math.sin(toRad(lat1));
  const sinLat2 = Math.sin(toRad(lat2));

  const x1 = earthRadius * cosLat1 * Math.cos(toRad(lon1));
  const y1 = earthRadius * cosLat1 * Math.sin(toRad(lon1));
  const z1 = earthRadius * sinLat1;

  const x2 = earthRadius * cosLat2 * Math.cos(toRad(lon2));
  const y2 = earthRadius * cosLat2 * Math.sin(toRad(lon2));
  const z2 = earthRadius * sinLat2;

  const dot = x1 * x2 + y1 * y2 + z1 * z2;
  const mag1 = Math.sqrt(x1 ** 2 + y1 ** 2 + z1 ** 2);
  const mag2 = Math.sqrt(x2 ** 2 + y2 ** 2 + z2 ** 2);

  const cosAngle = Math.min(1, Math.max(-1, dot / (mag1 * mag2)));
  return Math.round(Math.acos(cosAngle) * earthRadius);
}

export function getRadialCoordinates(centerPoint: number[], angle: number, distance: number): number[] {
  const [centerLon, centerLat] = centerPoint;

  if (distance === 0) {
    return [centerLon, centerLat];
  }

  let dist = distance;
  let bearing = angle;

  if (dist < 0) {
    dist = -dist;
    bearing += 180;
  }

  bearing = normalizeAngle(bearing);
  const westHemisphere = bearing > 180 ? -1 : 1;

  const theta = dist / earthRadius;
  const cosTheta = Math.cos(theta);
  const sinTheta = Math.sin(theta);

  const cosLat = Math.cos(toRad(centerLat));
  const sinLat = Math.sin(toRad(centerLat));
  const cosBearing = Math.cos(toRad(bearing));

  const sinResultLat = cosBearing * cosLat * sinTheta + sinLat * cosTheta;
  const resultLat = Math.asin(sinResultLat) * RAD_TO_DEG;

  const z = cosTheta * cosLat - cosBearing * sinTheta * sinLat;
  const n = Math.max(Math.sqrt(1 - sinResultLat ** 2), z);

  const resultLon = centerLon + Math.acos(z / n) * RAD_TO_DEG * westHemisphere;

  return [round(resultLon, 6), round(resultLat, 6)];
}

export function getBearing(centerPoint: number[], directionPoint: number[]): number {
  const [lon1, lat1] = centerPoint;
  const [lon2, lat2] = directionPoint;

  if (lat1 === lat2 && lon1 === lon2) {
    return 0;
  }

  const cosLat1 = Math.cos(toRad(lat1));
  const cosLat2 = Math.cos(toRad(lat2));
  const sinLat1 = Math.sin(toRad(lat1));
  const sinLat2 = Math.sin(toRad(lat2));
  const cosLonDiff = Math.cos(toRad(lon1 - lon2));

  const dot = cosLonDiff * cosLat1 * cosLat2 + sinLat1 * sinLat2;
  let z = cosLat1 * sinLat2 - cosLonDiff * cosLat2 * sinLat1;
  let n = Math.sqrt(1 - dot ** 2);

  if (Math.abs(n) < Math.abs(z)) {
    [z, n] = [n, z];
  }

  const angle = Math.acos(z / n) * RAD_TO_DEG;
  return lon1 > lon2 ? -angle : angle;
}

export function getBearing360(angle: number): number {
  return normalizeAngle(angle);
}

export function getBoundingRectangle(points: number[][]): number[][] {
  if (points.length === 0) {
    return [
      [0, 0],
      [0, 0],
    ];
  }

  let lonMin = Infinity;
  let latMin = Infinity;
  let lonMax = -Infinity;
  let latMax = -Infinity;

  for (const [lon, lat] of points) {
    if (lat > latMax) latMax = lat;
    if (lat < latMin) latMin = lat;
    if (lon > lonMax) lonMax = lon;
    if (lon < lonMin) lonMin = lon;
  }

  return [
    [lonMin, latMin],
    [lonMax, latMax],
  ];
}
